import React, { useState, useEffect } from "react";
import { Card, Form, Switch, Button, Space, message, Divider, Row, Col, Select } from "antd";
import { SaveOutlined, EditOutlined, CloseOutlined } from "@ant-design/icons";
import axios from "axios";
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";
import updateSellerMutation from "../queries/updateSellerMutation";

const MarketplaceSellerSettings = ({ seller, onUpdate }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  useEffect(() => {
    if (seller) {
      form.setFieldsValue({
        enabled: seller.enabled,
        public: seller.public,
        b2b: seller.b2b,
        b2c: seller.b2c,
        public_prices: seller.public_prices,
        ask_for_info: seller.ask_for_info,
        plan: seller.plan || "base",
      });
    }
  }, [seller, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      // Only send fields that are relevant to this form
      const fields = {
        enabled: values.enabled,
        public: values.public,
        b2b: values.b2b,
        b2c: values.b2c,
        public_prices: values.public_prices,
        ask_for_info: values.ask_for_info,
        plan: values.plan,
      };

      await axios.post(
        graphqlUrl,
        {
          query: updateSellerMutation,
          variables: { id: seller.objectId, fields },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success("Impostazioni aggiornate con successo");
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error updating seller settings:", error);
      message.error("Errore durante l'aggiornamento delle impostazioni");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.setFieldsValue({
      enabled: seller.enabled,
      public: seller.public,
      b2b: seller.b2b,
      b2c: seller.b2c,
      public_prices: seller.public_prices,
      ask_for_info: seller.ask_for_info,
      plan: seller.plan || "base",
    });
    setIsEditing(false);
  };

  return (
    <Card 
      title="Configurazione Profilo" 
      extra={
        !isEditing ? (
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={() => setIsEditing(true)}
          >
            Modifica
          </Button>
        ) : (
          <Space>
            <Button 
              icon={<CloseOutlined />} 
              onClick={handleCancel}
            >
              Annulla
            </Button>
            <Button 
              type="primary" 
              icon={<SaveOutlined />} 
              loading={loading}
              onClick={() => form.submit()}
            >
              Salva
            </Button>
          </Space>
        )
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={!isEditing}
      >
        <Divider orientation="left">Impostazioni di Visibilità</Divider>

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Attivo"
              name="enabled"
              valuePropName="checked"
              tooltip="Abilita o disabilita completamente il venditore"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Pubblico"
              name="public"
              valuePropName="checked"
              tooltip="Se attivo, il profilo sarà visibile pubblicamente"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Prezzi Pubblici"
              name="public_prices"
              valuePropName="checked"
              tooltip="Se attivo, i prezzi saranno visibili pubblicamente"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Servizi Offerti</Divider>

        <Row gutter={16}>
          <Col xs={24} sm={6}>
            <Form.Item
              label="B2B"
              name="b2b"
              valuePropName="checked"
              tooltip="Indica se è un fornitore di prodotti/servizi B2B"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item
              label="B2C"
              name="b2c"
              valuePropName="checked"
              tooltip="Indica se offre servizi funebri completi B2C"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Interazione con Clienti</Divider>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Richiesta Informazioni"
              name="ask_for_info"
              valuePropName="checked"
              tooltip="Se attivo, i clienti possono richiedere informazioni"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Piano</Divider>

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Piano"
              name="plan"
              tooltip="Il piano sottoscritto dal venditore"
            >
              <Select>
                <Select.Option value="base">Base</Select.Option>
                <Select.Option value="verified">Verificato</Select.Option>
                <Select.Option value="premium">Premium</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default MarketplaceSellerSettings;
